function hexToRgb(hex: string): [number, number, number] | null {
  const cleaned = hex.replace('#', '');
  const expanded =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned;

  if (expanded.length !== 6) return null;

  const num = parseInt(expanded, 16);
  if (Number.isNaN(num)) return null;

  return [Math.floor(num / 65536) % 256, Math.floor(num / 256) % 256, num % 256];
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((c) => {
      const hex = Math.max(0, Math.min(255, Math.round(c))).toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    })
    .join('')}`;
}

function relativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

export function getContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  if (!rgb1 || !rgb2) return 1;

  const l1 = relativeLuminance(...rgb1);
  const l2 = relativeLuminance(...rgb2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

const FALLBACK_COLOR = '#555555';

export function ensureContrast(
  accentColor: string,
  foregroundColor: string,
  minRatio = 3
): string {
  const rgb = hexToRgb(accentColor);
  if (!rgb) return FALLBACK_COLOR;

  if (getContrastRatio(accentColor, foregroundColor) >= minRatio) {
    return accentColor.length === 4 ? rgbToHex(...rgb) : accentColor.toLowerCase();
  }

  let [r, g, b] = rgb;
  for (let i = 0; i < 100; i++) {
    r = Math.max(0, r - 5);
    g = Math.max(0, g - 5);
    b = Math.max(0, b - 5);

    const candidate = rgbToHex(r, g, b);
    if (getContrastRatio(candidate, foregroundColor) >= minRatio) {
      return candidate;
    }
  }

  return FALLBACK_COLOR;
}
